import { Component, OnDestroy, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../../../../environments/environment';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CourseService } from '../../services/course.service';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { PrimaryButtonComponent } from '../../../../../shared/components/primary-button/primary-button.component';
import { CourseIconMappingService } from '../../../../../shared/services/course-icon-mapping.service';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { CourseChapter } from '../../models/Chapters';
import { LoadingComponent } from '../../../../../shared/components/loading/loading.component';
import { CurrentUserState } from '../../../../../core/store/current-user/current-user.state';
import { Store } from '@ngxs/store';
import { LearningPlanBuilderDataService } from '../../../learning-plan/services/learning-plan-builder-data.service';
import { CourseDetailsDTO } from '../../models/CourseDTO';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';

@Component({
  selector: 'app-course-detail-card',
  imports: [MatIcon, TranslocoPipe, PrimaryButtonComponent, LoadingComponent],
  templateUrl: './course-detail-card.component.html',
})
export class CourseDetailCardComponent implements OnDestroy {
  protected data = inject<{
    course: CourseDetailsDTO;
    isPrecuratedTraining: boolean;
  }>(MAT_DIALOG_DATA);
  protected readonly translocoService = inject(TranslocoService);
  protected dialogRef =
    inject<MatDialogRef<CourseDetailCardComponent>>(MatDialogRef);
  protected readonly sanitizer = inject(DomSanitizer);
  protected readonly http = inject(HttpClient);
  protected readonly courseService = inject(CourseService);
  protected readonly snackbarService = inject(SnackbarService);
  private readonly courseIconMappingService = inject(CourseIconMappingService);
  private readonly dataService = inject(LearningPlanBuilderDataService);
  private readonly store = inject(Store);
  private readonly analyticsService = inject(AnalyticsService);

  safeUrl: SafeResourceUrl;
  baseUrl = environment.backendConfig.baseUrl;
  chapters: CourseChapter[] = [];
  openChapters: number[] = [];
  private readonly previewOpenedAt = performance.now();

  constructor() {
    void this.initCoursePreview();
    this.loadCourseFromTranslation();
  }

  ngOnDestroy(): void {
    const engagementDurationSeconds = Math.round(
      (performance.now() - this.previewOpenedAt) / 1000,
    );
    this.analyticsService.track('course_preview_seen', {
      courseId: this.data.course.id,
      courseName: this.data.course.name,
      engagementDurationSeconds,
    });
  }

  async initCoursePreview() {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return;
    }
    const lang = this.translocoService.getActiveLang();
    try {
      await firstValueFrom(this.courseService.initCoursePreview());
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `${this.baseUrl}/subscriptions/${subscriptionId}/courses/${this.data.course.id}/preview/${lang}/story.html`,
      );
    } catch (error) {
      this.snackbarService.error((error as CustomPortalError).errorType);
    }
  }

  loadCourseFromTranslation() {
    const baseKey = 'portal.courses.courseTitleKeys.' + this.data.course.name;
    this.translocoService
      .selectTranslate(baseKey + '.chapters')
      .subscribe((data: CourseChapter[]) => {
        this.chapters = data;
      });
  }

  getCourseIcon(): string {
    return this.courseIconMappingService.getCourseIcon(
      this.data.course.name,
      this.data.course.topicAreaName,
    );
  }

  toggle(index: number) {
    const exists = this.openChapters.includes(index);

    if (exists) {
      this.openChapters = this.openChapters.filter((i) => i !== index);
    } else {
      this.openChapters.push(index);
    }
  }

  onCloseBtnClick() {
    this.dialogRef.close();
  }

  openCourseSetup(): void {
    this.analyticsService.track('course_selected', {
      courseName: this.data.course.name,
    });
    this.dialogRef.close();
    setTimeout(() => {
      this.dataService.reset();
      this.dataService.openDialog(this.data.course);
    }, 200);
  }
}
